import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Tables } from '@/integrations/supabase/types';
import { generateClientDealNumber } from '@/lib/dealNumbers';
import { canCreateDealForProperty } from '@/lib/deals';

export function useDeals() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['deals', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function usePendingDeals() {
  const { user, profile } = useAuth();
  return useQuery({
    queryKey: ['deals', 'pending', user?.id, profile?.role],
    enabled: !!user,
    queryFn: async () => {
      let query = supabase
        .from('deals')
        .select('*')
        .eq('status', 'pending-acceptance')
        .order('submitted_at', { ascending: false });

      if (profile?.role === 'lawyer') {
        query = query.or(`lawyer_id.eq.${user!.id},preferred_lawyer_id.eq.${user!.id}`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });
}

export function useDeal(dealId: string | undefined) {
  return useQuery({
    queryKey: ['deals', dealId],
    enabled: !!dealId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .eq('id', dealId!)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export function useDealByNumber(dealNumber: string | undefined) {
  return useQuery({
    queryKey: ['deals', 'number', dealNumber],
    enabled: !!dealNumber,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .eq('deal_number', dealNumber!)
        .single();
      if (error) throw error;
      return data;
    },
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (deal: Omit<Tables<'deals'>, 'id' | 'created_at' | 'updated_at' | 'deal_number'>) => {
      if (!user) {
        throw new Error('Not authenticated');
      }
      if (!deal.property_id) {
        throw new Error('Choose a listed property before creating a deal.');
      }
      if (!deal.lawyer_id && !deal.preferred_lawyer_id) {
        throw new Error('Assign a lawyer before creating a deal.');
      }

      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .select('id, title, address, state, lga, property_type, price, seller_name, deleted_at')
        .eq('id', deal.property_id)
        .maybeSingle();

      if (propertyError) throw propertyError;
      if (!property || property.deleted_at) {
        throw new Error('Choose a valid listed property before creating a deal.');
      }
      if (!canCreateDealForProperty(property, user?.id)) {
        throw new Error('You can only create deals for properties listed from your workspace.');
      }

      const { data, error } = await supabase
        .from('deals')
        .insert({
          deal_number: generateClientDealNumber(),
          ...deal,
          property_title: property.title,
          property_address: property.address,
          property_state: property.state,
          property_lga: property.lga,
          property_type: property.property_type,
          estimated_value: property.price,
          seller_name: deal.seller_name || property.seller_name || 'Seller',
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useUpdateDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Tables<'deals'>> & { id: string }) => {
      const { data, error } = await supabase.from('deals').update(updates).eq('id', id).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useAcceptDeal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (deal: Tables<'deals'>) => {
      if (!user) throw new Error('Not authenticated');
      if (deal.preferred_lawyer_id && deal.preferred_lawyer_id !== user.id && deal.lawyer_id !== user.id) {
        throw new Error('Only the lawyer assigned to this deal can accept it.');
      }

      // 1. Update deal status
      const { error: dealError } = await supabase
        .from('deals')
        .update({
          status: 'accepted',
          lawyer_id: user.id,
          preferred_lawyer_id: deal.preferred_lawyer_id || user.id,
          accepted_at: new Date().toISOString(),
        })
        .eq('id', deal.id);
      if (dealError) throw dealError;

      // 2. Create a matter from this deal
      const { data: matter, error: matterError } = await supabase
        .from('matters')
        .insert({
          deal_id: deal.id,
          lawyer_id: user.id,
          client_id: deal.client_id || null,
          agent_id: deal.agent_id,
          title: deal.property_title,
          property_address: deal.property_address,
          property_value: deal.estimated_value,
        })
        .select()
        .single();
      if (matterError) throw matterError;

      // 3. Notify the agent that their deal was accepted
      try {
        await supabase.functions.invoke('create-notification', {
          body: {
            user_id: deal.agent_id,
            title: 'Deal Accepted',
            message: `Your deal "${deal.property_title}" (${deal.deal_number}) has been accepted by a lawyer.`,
            type: 'deal',
            link_to: `/deal-status/${deal.id}`,
          },
        });
      } catch {
        // Notification is non-critical
      }

      return matter;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['matters'] });
    },
  });
}

export function useRejectDeal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, rejection_reason }: { id: string; rejection_reason: string }) => {
      const { data, error } = await supabase
        .from('deals')
        .update({ status: 'rejected', rejection_reason })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}
